import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useLocation, useNavigate } from "react-router-dom";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useConsumedLinkGuard } from "../hooks/useConsumedLinkGuard";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../components/BodyCopy";
import { Toast } from "../components/Toast";
import {
  BankIcon,
  Button,
  CashSpecialIcon,
  Layout,
  WalletIcon,
} from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { APP_ROUTES } from "../constants";
import { Popup } from "../components/Popup";
import {
  SelectFundingMethodSlot,
  type FlatFundingPick,
} from "../components/SelectFundingMethodSlot";
import { PayoutInfo } from "../components/PayoutInfo";
import { FirstSendWarning } from "../components/first-send/FirstSendWarning";
import { useFirstSendWarning } from "../hooks/useFirstSendWarning";
import { RouteUnavailablePage } from "./RouteUnavailablePage";
import { RouteQuoteUnavailableSheet } from "../components/RouteQuoteUnavailableSheet";
import { AmountOutOfRangeInfo } from "../components/AmountOutOfRangeInfo";
import { paymentType } from "../interfaces/payment";
import masterCardLogo from "../assets/master_card_icon.svg";
import visaLogo from "../assets/visa_color_icon.svg";
import { useTransferPricing } from "../hooks/useTransferPricing";
import { useFundingMethodLimits } from "../hooks/useFundingMethodLimits";
import { usePaymentMethods } from "../hooks/usePaymentMethods";
import { usePayerName } from "../hooks/usePayerName";
import { usePartnerLogoSrc } from "../hooks/usePartnerLogoSrc";
import {
  categoryFromFundingMethod,
  confirmationBankLabel,
  FUNDING_METHODS,
  isFundingMethod,
  optionForFundingMethod,
  type FundingMethod,
} from "../utils/catalog";
import {
  compareSendToBound,
  corridorsForFundingMethod,
  skusForCorridors,
} from "../utils/fundingLimits";
import { cancelLink, checkCipReadiness, isDeadLinkError } from "../services/api";
import { prepareFundingRail } from "../utils/prepare-funding-rail";
import { CvvPrepareGate } from "../components/CvvPrepareGate";
import { UnavailableFundingCardSheet } from "../components/UnavailableFundingCardSheet";
import type { ICardPaymentMethod } from "../interfaces/payment-method";
import {
  reasonFromCardPayCode,
  type UnavailableCardReason,
} from "../utils/card-funding-availability";
import { resolveFundingCardFromState } from "../utils/resolve-funding-card";
import {
  catalogFundingMethodOf,
  type AddCardClassNotice,
} from "../utils/resolve-add-card-funding-class";
import type {
  FundingInstrument,
  FundingInstrumentSaved,
} from "../interfaces/funding";
import { isFundingInstrumentReady } from "../interfaces/funding";
import { getEvents, safeCapture } from "../analytics";
import { quoteMatchesSelection } from "../utils/quoteMatchesSelection";
import { useQuoteLoadingTimedOut } from "../hooks/useQuoteLoadingTimedOut";
import { formatLabeledAmount } from "../utils";
import { countryDisplayName } from "../utils/country-display";
import { formatPartyName, recipientFirstName } from "../utils/partyName";
import { isHttpUrl, returnToChat } from "../utils/returnToChat";
import type { IPayoutPaymentMethod } from "../shared/session/interfaces/session";
import { Navbar } from "../components/Navbar";

type PaymentPopup = "funding" | "amountLimit" | null;

const sheetCardFromNotice = (
  card: AddCardClassNotice["card"]
): ICardPaymentMethod => ({
  paymentMethodId: card.paymentMethodId,
  brand: card.brand,
  last4: card.last4,
  expiryMonth: "",
  expiryYear: "",
  enabled: true,
  isDefault: false,
  fundingMethod: card.fundingMethod,
  productClass: card.productClass,
});

export const SummaryPage = memo(() => {
  useConsumedLinkGuard();
  const { t, i18n } = useTranslation();
  const {
    theme,
    paymentState,
    setQuoteState,
    setPaymentState,
    fundingInstrument,
    setFundingInstrument,
    setPreparedFundingPaymentMethodId,
    pendingFundingMethod,
    setPendingFundingMethod,
  } = useStore();
  const { session } = useSessionStore();
  const colorConfig = theme.whiteView;
  const { backgroundBtn, textColorBtn, title, bodyCopy, footerColor } =
    colorConfig;
  const errorColor = theme.errorView?.title ?? title;
  const navigate = useNavigate();
  const location = useLocation();
  const { redirectTo } = useRouteWithToken();
  const cardClassNotice = (
    location.state as { cardClassNotice?: AddCardClassNotice } | null
  )?.cardClassNotice;
  const cardClassNoticeHandledRef = useRef(false);

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("summary"));
  }, []);

  const [popup, setPopup] = useState<PaymentPopup>(null);
  const [showRouteUnavailable, setShowRouteUnavailable] = useState(false);
  const [routeQuoteDismissedFor, setRouteQuoteDismissedFor] = useState<
    string | null
  >(null);
  const [routeQuoteFor, setRouteQuoteFor] = useState<string | null>(null);
  const [shake, setShake] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [prepareError, setPrepareError] = useState<string | null>(null);
  const [cvvGate, setCvvGate] = useState<{
    last4: string;
    brand?: string;
    funding: FundingInstrumentSaved;
    error: string | null;
  } | null>(null);
  const [blockedFundingCard, setBlockedFundingCard] =
    useState<ICardPaymentMethod | null>(null);
  const [blockedReason, setBlockedReason] =
    useState<UnavailableCardReason | null>(null);
  const [blockedRailCards, setBlockedRailCards] = useState<
    ReadonlyMap<string, UnavailableCardReason>
  >(() => new Map());
  const [classChangedToast, setClassChangedToast] = useState<
    "DEBIT_CARD" | "CREDIT_CARD" | null
  >(null);

  useEffect(() => {
    const cardId =
      paymentState.payment?.type === paymentType.CARD
        ? paymentState.payment.cardId
        : null;
    if (!cardId || !blockedRailCards.has(cardId)) return;
    setFundingInstrument(null);
    setPaymentState({ payment: null });
  }, [
    blockedRailCards,
    paymentState.payment,
    setFundingInstrument,
    setPaymentState,
  ]);

  const config = session.data?.config;
  const { src: partnerLogoSrc } = usePartnerLogoSrc(session.data?.linkId);

  const prepareErrorMessage = useCallback(
    (code: string | undefined): string => {
      switch (code) {
        case "PROVISION_FAILED":
          return t("transfer.prepare.errorProvision");
        case "CVV_REQUIRED":
          return t("transfer.prepare.errorCvv");
        case "REGISTER_FAILED":
          return t("transfer.prepare.errorRegister");
        case "READINESS_FAILED":
          return t("transfer.prepare.errorReadiness");
        default:
          return t("transfer.prepare.errorGeneric");
      }
    },
    [t],
  );

  const {
    amount: pricingAmount,
    sourceCountry,
    destinationCountry,
    sourceCurrency,
    destinationCurrency,
    availableFundingMethods,
    options,
    selectedOption,
    capabilityKey,
    quote,
    quoteLoading,
    quoteError,
    quoteErrorKind,
    refetchQuote,
    preQuoteLoading,
    routeUnavailable,
    payerCode,
    payoutMethod,
    economics,
    facets,
  } = useTransferPricing();

  useEffect(() => {
    if (cardClassNoticeHandledRef.current) return;
    if (!cardClassNotice) return;
    if (cardClassNotice.kind === "changed" && availableFundingMethods.length === 0) {
      return;
    }

    cardClassNoticeHandledRef.current = true;
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });

    if (cardClassNotice.kind === "unclassified") {
      setBlockedReason(null);
      setBlockedFundingCard(sheetCardFromNotice(cardClassNotice.card));
      return;
    }

    if (!availableFundingMethods.includes(cardClassNotice.fundingMethod)) {
      setPendingFundingMethod(null);
      setFundingInstrument(null);
      setPaymentState({ payment: null });
      setBlockedReason(null);
      setBlockedFundingCard(sheetCardFromNotice(cardClassNotice.card));
      return;
    }

    setClassChangedToast(cardClassNotice.fundingMethod);
  }, [
    availableFundingMethods,
    cardClassNotice,
    location.pathname,
    location.search,
    navigate,
    setFundingInstrument,
    setPaymentState,
    setPendingFundingMethod,
  ]);

  const payerLookup = usePayerName(payerCode, destinationCountry, payoutMethod);
  const payerName = payerLookup.name;

  const queriesForMethod = useCallback(
    (method: FundingMethod) => {
      const priced = optionForFundingMethod(options, method);
      const corridors = corridorsForFundingMethod(method, {
        options,
        facets,
      });
      return skusForCorridors(method, corridors, {
        payoutMethod,
        payerCode,
        customerFee: priced?.customerFee ?? null,
      });
    },
    [facets, options, payerCode, payoutMethod],
  );
  const { details: fundingDetails, loading: fundingLimitsLoading } =
    useFundingMethodLimits(
      availableFundingMethods,
      queriesForMethod,
      sourceCountry,
      destinationCountry,
      sourceCurrency,
    );

  const sendForBound =
    quote?.sendAmount ?? selectedOption?.sendAmount ?? economics?.sendAmount ?? null;
  const firstSend = useFirstSendWarning({
    linkId: session.data?.linkId,
    sendAmount: sendForBound,
    sourceCurrency,
    fundingMethod: pendingFundingMethod,
  });
  const selectedFundingDetails = isFundingMethod(pendingFundingMethod)
    ? fundingDetails[pendingFundingMethod]
    : undefined;
  const amountBoundVerdict = compareSendToBound(
    sendForBound,
    selectedFundingDetails,
  );
  const amountOutOfRange =
    amountBoundVerdict === "below_min" || amountBoundVerdict === "above_max";

  const {
    cards: registeredCards,
    bankAccounts,
    lastUsedFunding,
  } = usePaymentMethods(session.data?.linkId);

  const resolveFundingInstrument = useCallback((): FundingInstrument | null => {
    if (fundingInstrument && isFundingInstrumentReady(fundingInstrument)) {
      return fundingInstrument;
    }
    if (
      paymentState.payment?.type === paymentType.CARD &&
      paymentState.payment.cardId
    ) {
      const card = registeredCards.find(
        (c) => c.paymentMethodId === paymentState.payment!.cardId,
      );
      if (card) {
        return {
          source: "saved",
          paymentMethodId: card.paymentMethodId,
          brand: card.brand,
          last4: card.last4,
        };
      }
    }
    if (
      paymentState.payment?.type === paymentType.CARD &&
      fundingInstrument?.source === "new"
    ) {
      return fundingInstrument;
    }
    return null;
  }, [fundingInstrument, paymentState.payment, registeredCards]);

  useEffect(() => {
    if (routeUnavailable) setShowRouteUnavailable(true);
  }, [routeUnavailable]);

  const pendingFundingMethodRef = useRef(pendingFundingMethod);
  pendingFundingMethodRef.current = pendingFundingMethod;

  useEffect(() => {
    if (quoteLoading) return;
    if (quoteErrorKind === "route" && pendingFundingMethod) {
      setRouteQuoteFor(pendingFundingMethod);
      return;
    }
    setRouteQuoteFor(null);
  }, [quoteLoading, quoteErrorKind, pendingFundingMethod]);

  const dismissRouteQuoteDrawer = useCallback(() => {
    const method = pendingFundingMethodRef.current;
    if (method) setRouteQuoteDismissedFor(method);
  }, []);

  const chooseAnotherFundingMethod = useCallback(() => {
    const method = pendingFundingMethodRef.current;
    if (method) setRouteQuoteDismissedFor(method);
    setPopup("funding");
  }, []);

  useEffect(() => {
    if (routeUnavailable) return;
    if (paymentState.payment || availableFundingMethods.length !== 1) return;
    const [only] = availableFundingMethods;
    if (only === "CASH_DEPOSIT") {
      setFundingInstrument(null);
      setPendingFundingMethod("CASH_DEPOSIT");
      setPaymentState({
        payment: {
          cardId: null,
          type: paymentType.CASH,
          brand: t("payment.cashDeposit"),
        },
      });
    } else if (only === "ACH" || only === "RTP" || only === "BANK_BALANCE") {
      setPendingFundingMethod(only);
      setPaymentState({
        payment: {
          cardId: null,
          type: paymentType.BANK,
          brand: t("transfer.bankAccount", { last4: "" }).trim(),
        },
      });
    }
  }, [
    availableFundingMethods,
    paymentState.payment,
    setPaymentState,
    setFundingInstrument,
    setPendingFundingMethod,
    routeUnavailable,
    t,
  ]);

  useEffect(() => {
    if (routeUnavailable) return;
    if (paymentState.payment) return;
    if (!lastUsedFunding) return;
    if (!isFundingMethod(lastUsedFunding.method)) return;
    if (!availableFundingMethods.includes(lastUsedFunding.method)) return;

    const lastCategory = categoryFromFundingMethod(lastUsedFunding.method);

    if (lastUsedFunding.method === "CASH_DEPOSIT") {
      setPendingFundingMethod("CASH_DEPOSIT");
      setPaymentState({
        payment: {
          cardId: null,
          type: paymentType.CASH,
          brand: t("payment.cashDeposit"),
        },
      });
      return;
    }

    if (lastCategory === "CARD" && lastUsedFunding.paymentMethodId) {
      const card = registeredCards.find(
        (c) => c.paymentMethodId === lastUsedFunding.paymentMethodId,
      );
      if (card) {
        setPendingFundingMethod(lastUsedFunding.method);
        setFundingInstrument({
          source: "saved",
          paymentMethodId: card.paymentMethodId,
          brand: card.brand,
          last4: card.last4,
        });
        setPaymentState({
          payment: {
            cardId: card.paymentMethodId,
            type: paymentType.CARD,
            brand: `${(card.brand || "").toUpperCase()}-${card.last4}`,
          },
        });
      }
      return;
    }

    if (lastCategory === "ACCOUNT") {
      const account = lastUsedFunding.paymentMethodId
        ? bankAccounts.find(
            (a) => a.paymentMethodId === lastUsedFunding.paymentMethodId,
          )
        : undefined;
      setPendingFundingMethod(lastUsedFunding.method);
      setPaymentState({
        payment: {
          cardId: account?.paymentMethodId ?? null,
          type: paymentType.BANK,
          brand: account
            ? `${account.bankName} *${account.maskedAccountNumber.slice(-4)}`
            : t("transfer.bankAccount", { last4: "" }).trim(),
        },
      });
    }
  }, [
    lastUsedFunding,
    registeredCards,
    bankAccounts,
    availableFundingMethods,
    paymentState.payment,
    setPaymentState,
    setFundingInstrument,
    setPendingFundingMethod,
    routeUnavailable,
    t,
  ]);

  useEffect(() => {
    if (!pendingFundingMethod || availableFundingMethods.length === 0) return;
    if (
      !availableFundingMethods.includes(
        pendingFundingMethod as (typeof FUNDING_METHODS)[number],
      )
    ) {
      setPendingFundingMethod(null);
      if (paymentState.payment != null) {
        setPaymentState({ payment: null });
      }
      setFundingInstrument(null);
    }
  }, [
    availableFundingMethods,
    pendingFundingMethod,
    paymentState.payment,
    setPaymentState,
    setFundingInstrument,
    setPendingFundingMethod,
  ]);

  const handleFundingPick = useCallback(
    (pick: FlatFundingPick) => {
      // Same method does not refetch the quote. Clear the dismiss so a known
      // route failure shows the drawer again.
      setRouteQuoteDismissedFor(null);
      if (pick.kind === "add") {
        setPendingFundingMethod(pick.method);
        setFundingInstrument(null);
        safeCapture(getEvents().FUNDING_SELECTED, {
          funding_method: pick.method,
        });
        setPopup(null);
        redirectTo(APP_ROUTES.ADD_CARD_PAGE);
        return;
      }
      if (pick.kind === "card") {
        const classified = catalogFundingMethodOf(pick.card.fundingMethod);
        const routeCardSku =
          pendingFundingMethod === "DEBIT_CARD" ||
          pendingFundingMethod === "CREDIT_CARD"
            ? pendingFundingMethod
            : availableFundingMethods.find(
                (m): m is "DEBIT_CARD" | "CREDIT_CARD" =>
                  m === "DEBIT_CARD" || m === "CREDIT_CARD",
              );
        const method =
          classified && availableFundingMethods.includes(classified)
            ? classified
            : routeCardSku;
        if (!method) return;
        setPendingFundingMethod(method);
        setFundingInstrument({
          source: "saved",
          paymentMethodId: pick.card.paymentMethodId,
          brand: pick.card.brand,
          last4: pick.card.last4,
          fundingMethod: method,
        });
        setPaymentState({
          payment: {
            cardId: pick.card.paymentMethodId,
            type: paymentType.CARD,
            brand: `${(pick.card.brand || "").toUpperCase()}-${pick.card.last4}`,
          },
        });
        safeCapture(getEvents().FUNDING_SELECTED, { funding_method: method });
        setPopup(null);
        return;
      }
      if (pick.kind === "account") {
        const method =
          availableFundingMethods.find(
            (m) => m === "ACH" || m === "RTP" || m === "BANK_BALANCE",
          ) ?? "ACH";
        setPendingFundingMethod(method);
        setFundingInstrument(null);
        setPaymentState({
          payment: {
            cardId: pick.account.paymentMethodId,
            type: paymentType.BANK,
            brand: `${pick.account.bankName} *${pick.account.maskedAccountNumber.slice(-4)}`,
          },
        });
        safeCapture(getEvents().FUNDING_SELECTED, { funding_method: method });
        setPopup(null);
        return;
      }
      const method = pick.kind === "cash" ? "CASH_DEPOSIT" : pick.method;
      setPendingFundingMethod(method);
      setFundingInstrument(null);
      if (method === "CASH_DEPOSIT") {
        setPaymentState({
          payment: {
            cardId: null,
            type: paymentType.CASH,
            brand: t("payment.cashDeposit"),
          },
        });
      } else {
        setPaymentState({
          payment: {
            cardId: null,
            type: paymentType.BANK,
            brand: t("transfer.bankAccount", { last4: "" }).trim(),
          },
        });
      }
      safeCapture(getEvents().FUNDING_SELECTED, { funding_method: method });
      setPopup(null);
    },
    [
      availableFundingMethods,
      pendingFundingMethod,
      redirectTo,
      setFundingInstrument,
      setPaymentState,
      setPendingFundingMethod,
      t,
    ],
  );

  const paymentMethodInvalid = !!paymentState.payment && !capabilityKey;
  const quoteLoadingTimedOut = useQuoteLoadingTimedOut(quoteLoading);
  const quoteFailed = Boolean(quoteError) || quoteLoadingTimedOut;
  const routeQuoteBlocked =
    !routeUnavailable &&
    !quoteLoading &&
    quoteErrorKind === "route" &&
    Boolean(pendingFundingMethod) &&
    routeQuoteFor === pendingFundingMethod;
  const showRouteQuoteDrawer =
    routeQuoteBlocked && routeQuoteDismissedFor !== pendingFundingMethod;
  const amountReady = !!pricingAmount;
  const paymentReady = Boolean(
    paymentState.payment &&
      (paymentState.payment.type !== paymentType.CARD ||
        cvvGate?.funding ||
        resolveFundingInstrument()),
  );
  const quoteMatches = quoteMatchesSelection(quote, quoteLoading, {
    fundingMethod: pendingFundingMethod,
    capabilityKey: selectedOption?.capabilityKey ?? capabilityKey,
  });
  const pricingLoading = preQuoteLoading || quoteLoading;
  const blockForPricing =
    amountReady && paymentReady && !quoteMatches && !quoteFailed;
  const canConfirm =
    amountReady &&
    paymentReady &&
    quoteMatches &&
    !paymentMethodInvalid &&
    !routeUnavailable;
  const showRainbow =
    (canConfirm || confirmLoading) &&
    !fundingLimitsLoading &&
    !amountOutOfRange;
  const payDisabled =
    confirmLoading ||
    cancelLoading ||
    !paymentReady ||
    pricingLoading ||
    blockForPricing ||
    quoteFailed;
  const nudgeIncompleteCompose = useCallback(() => {
    setShake(true);
    window.setTimeout(() => setShake(false), 500);
  }, []);

  const handleConfirmClick = useCallback(
    async (cvv?: string) => {
      if (confirmLoading) return;

      if (!amountReady || !paymentReady || !paymentState.payment) {
        nudgeIncompleteCompose();
        return;
      }
      if (routeUnavailable || paymentMethodInvalid) return;
      if (fundingLimitsLoading) return;
      if (amountOutOfRange) {
        setPopup("amountLimit");
        return;
      }
      if (!quoteMatches || !quote || !capabilityKey) {
        return;
      }

      const payment = paymentState.payment;
      const linkId = session.data?.linkId;
      if (!linkId) return;

      setConfirmLoading(true);
      setPrepareError(null);
      safeCapture(getEvents().CONTINUE_CLICK);

      try {
        setQuoteState(quote);

        let preparedFundingId: string | undefined;

        if (payment.type === paymentType.CARD) {
          const instrument = cvvGate?.funding ?? resolveFundingInstrument();
          if (!instrument) {
            nudgeIncompleteCompose();
            return;
          }

          const quoteFundingMethod =
            pendingFundingMethod === "DEBIT_CARD" ||
            pendingFundingMethod === "CREDIT_CARD"
              ? pendingFundingMethod
              : undefined;
          const fundingResult = await prepareFundingRail({
            linkId,
            funding: instrument,
            capabilityKey,
            cvv,
            fundingMethod: quoteFundingMethod,
          });

          setFundingInstrument(fundingResult.funding);
          preparedFundingId = fundingResult.funding.paymentMethodId;

          setPaymentState({
            payment: {
              cardId: fundingResult.funding.paymentMethodId,
              type: paymentType.CARD,
              brand: `${(fundingResult.funding.brand || "").toUpperCase()}-${fundingResult.funding.last4}`,
            },
          });

          if (fundingResult.status === "needs_cvv") {
            setCvvGate({
              last4: fundingResult.funding.last4,
              brand: fundingResult.funding.brand,
              funding: fundingResult.funding,
              error:
                fundingResult.errorCode &&
                fundingResult.errorCode !== "CVV_REQUIRED"
                  ? prepareErrorMessage(fundingResult.errorCode)
                  : null,
            });
            return;
          }

          if (fundingResult.status === "failed") {
            const reason = reasonFromCardPayCode(
              fundingResult.errorCode,
              fundingResult.retryLater,
            );
            if (reason) {
              const listed = resolveFundingCardFromState(
                payment,
                fundingResult.funding,
                registeredCards,
              );
              if (
                reason === "railRejected" ||
                reason === "railTemporarilyUnavailable"
              ) {
                const paymentMethodId = fundingResult.funding.paymentMethodId;
                setBlockedRailCards((prev) => {
                  const next = new Map(prev);
                  next.set(paymentMethodId, reason);
                  return next;
                });
                setFundingInstrument(null);
                setPaymentState({ payment: null });
                safeCapture(getEvents().CARD_UNAVAILABLE_SHOWN, { reason });
              }
              setBlockedReason(reason);
              setBlockedFundingCard(
                listed ?? {
                  paymentMethodId: fundingResult.funding.paymentMethodId,
                  last4: fundingResult.funding.last4,
                  brand: fundingResult.funding.brand,
                  expiryMonth: "",
                  expiryYear: "",
                  enabled: true,
                  isDefault: false,
                  fundingMethod: fundingResult.funding.fundingMethod,
                },
              );
              return;
            }
            setPrepareError(prepareErrorMessage(fundingResult.errorCode));
            return;
          }

          setCvvGate(null);
        }

        setPreparedFundingPaymentMethodId(preparedFundingId ?? null);

        const cipResult = await checkCipReadiness(linkId);

        if (cipResult.shouldRunCip && cipResult.cipTicketId) {
          redirectTo(APP_ROUTES.CIP_PAGE, {
            replace: true,
            state: { cipTicketId: cipResult.cipTicketId },
          });
          return;
        }

        redirectTo(APP_ROUTES.SENDING_MONEY_PAGE, { replace: true });
      } catch (err) {
        if (isDeadLinkError(err)) {
          return;
        }
        const code =
          err && typeof err === "object" && "code" in err
            ? String((err as { code?: string }).code)
            : undefined;
        if (code === "REGISTER_FAILED") {
          setPrepareError(prepareErrorMessage(code));
          return;
        }
        console.error("[SummaryPage] Confirm failed:", err);
        navigate(APP_ROUTES.ERROR_PAGE, {
          state: {
            title: t("transfer.error"),
            subTitle: null,
            message:
              err instanceof Error ? err.message : t("error.tryAgainLater"),
            btnText: t("common.retry"),
            isOut: false,
            pageUrl: APP_ROUTES.SUMMARY_PAGE,
          },
        });
      } finally {
        setConfirmLoading(false);
      }
    },
    [
      confirmLoading,
      amountReady,
      paymentReady,
      paymentState.payment,
      paymentMethodInvalid,
      routeUnavailable,
      quoteMatches,
      quote,
      capabilityKey,
      fundingLimitsLoading,
      amountOutOfRange,
      session.data?.linkId,
      setQuoteState,
      setPreparedFundingPaymentMethodId,
      setFundingInstrument,
      setPaymentState,
      resolveFundingInstrument,
      cvvGate,
      redirectTo,
      navigate,
      prepareErrorMessage,
      nudgeIncompleteCompose,
      t,
      fundingInstrument,
      registeredCards,
      pendingFundingMethod,
      setPaymentState,
    ],
  );

  const handleCancel = useCallback(async () => {
    if (cancelLoading) return;
    setCancelLoading(true);
    const linkId = session.data?.linkId;
    const chatUrl = config?.returnToChatUrl;
    try {
      if (linkId) {
        await cancelLink(linkId, { reason: "user_cancel" });
      }
    } catch (err) {
      console.error("[SummaryPage] Cancel failed (leaving anyway):", err);
    } finally {
      if (isHttpUrl(chatUrl)) {
        returnToChat(chatUrl);
      }
      setCancelLoading(false);
    }
  }, [cancelLoading, session.data?.linkId, config?.returnToChatUrl]);

  const pickIcon = useCallback((type: paymentType, brand: string) => {
    switch (type) {
      case paymentType.CARD: {
        let finalIcon = null;
        if (brand.toLocaleUpperCase().includes("VISA")) finalIcon = visaLogo;
        if (brand.toLocaleUpperCase().includes("MASTERCARD"))
          finalIcon = masterCardLogo;
        return {
          brand,
          icon: <img src={finalIcon ?? ""} alt={`${brand} logo`} />,
        };
      }
      case paymentType.BANK:
        return { brand, icon: <BankIcon size={20} /> };
      case paymentType.CASH:
        return { brand, icon: <CashSpecialIcon size={20} /> };
      default:
        return { brand, icon: <WalletIcon size={20} /> };
    }
  }, []);

  const paymentSelected = useMemo(
    () =>
      paymentState.payment
        ? pickIcon(paymentState.payment.type, paymentState.payment.brand)
        : {
            brand: t("transfer.selectPaymentMethod"),
            icon: <WalletIcon size={20} />,
          },
    [paymentState, pickIcon, t],
  );

  const recipient = config?.recipient;
  const payout: IPayoutPaymentMethod | undefined =
    config?.userData?.paymentMethod ?? recipient?.payoutMethod;
  const canonicalPayout =
    payout?.payoutMethod ??
    (payout?.type === "bank_account"
      ? "BANK_ACCOUNT"
      : payout?.type === "cash"
        ? "CASH_PICKUP"
        : payout?.type);
  const isBank =
    canonicalPayout === "BANK_ACCOUNT" || payout?.type === "bank_account";
  const isCash =
    canonicalPayout === "CASH_PICKUP" || payout?.type === "cash";

  const destName = countryDisplayName(
    recipient?.address?.country || destinationCountry,
    i18n.language,
  );
  const firstName = recipientFirstName(recipient);

  const money = (value: number | undefined, currency: string, digits = 2) => {
    if (value == null) return "";
    return formatLabeledAmount(value, currency, {
      country: "en-US",
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
  };

  const sendLabel = quote
    ? money(quote.sendAmount, quote.sourceCurrency)
    : economics
      ? money(economics.sendAmount, sourceCurrency)
      : "";
  const rateLabel = quote
    ? money(quote.exchangeRate, quote.destinationCurrency, 4)
    : economics
      ? money(economics.exchangeRate, destinationCurrency, 4)
      : "";
  const feeLabel = quote
    ? money(quote.totalFee ?? quote.fee ?? 0, quote.sourceCurrency)
    : economics
      ? money(economics.fee, sourceCurrency)
      : "";
  const receiveLabel = quote
    ? money(quote.receiveAmount, quote.destinationCurrency)
    : economics
      ? money(economics.receiveAmount, destinationCurrency)
      : "";
  const totalLabel = quote
    ? money(quote.totalCost, quote.sourceCurrency)
    : economics
      ? money(economics.totalCost, sourceCurrency)
      : "";

  const payoutMethodLabel = isBank
    ? t("confirmation.bankMethod")
    : isCash
      ? t("confirmation.pickupMethod")
      : "";
  const payerRowLabel = isBank
    ? t("confirmation.bank")
    : t("confirmation.pickupAt");
  const payerRowValue = isBank
    ? confirmationBankLabel({
        catalogName: payerName,
        bankName: payout?.bankAccount?.bankName,
        payerCode: payout?.bankAccount?.payer ?? payerCode,
        bankCode: payout?.bankAccount?.bankCode,
      })
    : payerName || payout?.cash?.payer;
  const accountNumber = isBank ? payout?.bankAccount?.accountNumber : undefined;

  const showEstimated = Boolean(economics?.estimated && !quote);
  const pricingPending = Boolean(
    pricingAmount && (quoteLoading || preQuoteLoading || !economics),
  );
  const pendingValue = (value: string) => !value && pricingPending;

  const summaryRows = [
    {
      key: "send",
      label: t("confirmation.amountToSend"),
      value: sendLabel,
      pending: pendingValue(sendLabel),
    },
    {
      key: "rate",
      label: t("confirmation.exchangeRate"),
      value: rateLabel,
      pending: pendingValue(rateLabel),
    },
    {
      key: "fee",
      label: t("confirmation.fee"),
      value: feeLabel,
      pending: pendingValue(feeLabel),
    },
    {
      key: "receive",
      label: t("confirmation.receives", {
        name: firstName || t("confirmation.recipient"),
      }),
      value: receiveLabel,
      pending: pendingValue(receiveLabel),
    },
    { key: "payout", label: t("confirmation.payoutMethod"), value: payoutMethodLabel },
    {
      key: "name",
      label: t("confirmation.fullName"),
      value: formatPartyName(recipient?.name),
    },
    { key: "dest", label: t("confirmation.destination"), value: destName },
    {
      key: "payer",
      label: payerRowLabel,
      value: payerRowValue,
      pending:
        !payerRowValue &&
        ((isBank && payerLookup.pending) ||
          ((isBank || isCash) && pricingPending)),
    },
    { key: "account", label: t("confirmation.accountNumber"), value: accountNumber },
    {
      key: "total",
      label: t("confirmation.totalPayment"),
      value: totalLabel,
      isStrong: true,
      pending: pendingValue(totalLabel),
    },
  ].filter((row) => Boolean(row.label && (row.value || row.pending)));

  if (showRouteUnavailable) {
    const canReturnToChat = isHttpUrl(config?.returnToChatUrl);
    return (
      <RouteUnavailablePage
        destinationCountry={destinationCountry}
        payerCode={payerCode}
        payerName={isBank ? payerRowValue : payerName}
        backLabel={canReturnToChat ? t("routeUnavailable.backToChat") : undefined}
        closing={cancelLoading}
        onClose={
          canReturnToChat ? handleCancel : () => setShowRouteUnavailable(false)
        }
      />
    );
  }

  return (
    <>
      <AuraLayout colorConfig={colorConfig} className="lola-confirmation-page">
        <Layout.Header>
          <Navbar
            color={bodyCopy}
            title={t("confirmation.title")}
            align="center"
            onBackClick={() => navigate(-1)}
          />
        </Layout.Header>
        <Layout.Content className="lola-confirmation-section">
          <div
            className="lola-confirmation-card"
            aria-busy={pricingPending}
          >
            {summaryRows.map((field) => (
              <div key={field.key} className="lola-confirmation-row">
                <p
                  className={
                    field.isStrong
                      ? "lola-confirmation-label lola-confirmation-strong"
                      : "lola-confirmation-label"
                  }
                >
                  {field.label}:
                </p>
                <p
                  className={
                    field.isStrong
                      ? "lola-confirmation-value lola-confirmation-strong"
                      : "lola-confirmation-value"
                  }
                  aria-label={
                    field.pending ? t("transfer.loadingQuote") : undefined
                  }
                >
                  {field.pending ? (
                    <span
                      className="lola-confirmation-skeleton"
                      aria-hidden="true"
                    />
                  ) : (
                    field.value
                  )}
                </p>
              </div>
            ))}
            <div
              className={`lola-confirmation-estimated${
                showEstimated ? " is-visible" : ""
              }`}
              aria-hidden={!showEstimated}
            >
              <div className="lola-confirmation-estimated-inner">
                <BodyCopy className="footer-text" textColor={bodyCopy}>
                  {t("confirmation.estimated")}
                </BodyCopy>
              </div>
            </div>
          </div>

          <PayoutInfo
            title={t("transfer.payingWith")}
            subtitle={paymentSelected.brand}
            icon={paymentSelected.icon}
            background={backgroundBtn}
            color={textColorBtn}
            onChange={() => setPopup("funding")}
            className={`lola-confirmation-funding ${
              !paymentReady ? "payment-required" : ""
            } ${shake && !paymentReady ? "shake" : ""}`.trim()}
          />
          {firstSend.visible ? (
            <FirstSendWarning
              className="lola-confirmation-first-send"
              title={firstSend.title}
              body={firstSend.body}
            />
          ) : null}
          {paymentMethodInvalid && (
            <BodyCopy className="payment-error-text" textColor={errorColor}>
              {t("transfer.paymentMethodUnavailable")}
            </BodyCopy>
          )}
          {routeQuoteBlocked && !showRouteQuoteDrawer ? (
            <BodyCopy
              className="payment-error-text"
              textColor={errorColor}
              role="alert"
            >
              {(isBank ? payerRowValue : payerName)?.trim()
                ? t("routeUnavailable.methodDrawerMessagePayer", {
                    payer: (isBank ? payerRowValue : payerName)?.trim(),
                    country: countryDisplayName(
                      destinationCountry,
                      i18n.language,
                    ),
                  })
                : t("routeUnavailable.methodDrawerMessage", {
                    country: countryDisplayName(
                      destinationCountry,
                      i18n.language,
                    ),
                  })}
            </BodyCopy>
          ) : null}
          {quoteFailed && quoteErrorKind !== "route" ? (
            <>
              <BodyCopy
                className="payment-error-text"
                textColor={errorColor}
                role="alert"
              >
                {t("transfer.quoteError")}
              </BodyCopy>
              <Button
                variant="cancel"
                size="small"
                color={bodyCopy}
                onClick={() => {
                  void refetchQuote();
                }}
              >
                {t("transfer.quoteRetry")}
              </Button>
            </>
          ) : null}
          {prepareError ? (
            <BodyCopy
              className="payment-error-text"
              textColor={errorColor}
              role="alert"
            >
              {prepareError}
            </BodyCopy>
          ) : null}

          <section className="lola-confirmation-operated">
            <BodyCopy
              className="lola-confirmation-operated-label"
              textColor={footerColor}
            >
              {t("confirmation.operatedBy")}
            </BodyCopy>
            {partnerLogoSrc ? (
              <img
                src={partnerLogoSrc}
                alt={session.data?.partner?.name ?? t("confirmation.operatedBy")}
                className="lola-confirmation-logo"
              />
            ) : null}
          </section>
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section lola-confirmation-footer">
            <Button
              variant="cancel"
              size="small"
              color={bodyCopy}
              className="lola-confirmation-cancel"
              disabled={confirmLoading || cancelLoading}
              style={{width: "fit-content"}}
              onClick={() => {
                void handleCancel();
              }}
            >
              {t("common.cancel")}
            </Button>
            <div
              className={`button-wrapper ${showRainbow ? "rainbow-ready" : ""}`}
            >
              <Button
                size="large"
                background={backgroundBtn}
                color={textColorBtn}
                disabled={payDisabled}
                onClick={() => {
                  void handleConfirmClick();
                }}
              >
                {confirmLoading
                  ? t("transfer.prepare.loading")
                  : pricingLoading || blockForPricing
                    ? t("transfer.loadingQuote")
                    : t("common.pay")}
              </Button>
            </div>
          </section>
        </Layout.Footer>
      </AuraLayout>
      <Popup
        visible={showRouteQuoteDrawer}
        onClose={dismissRouteQuoteDrawer}
      >
        <RouteQuoteUnavailableSheet
          destinationCountry={destinationCountry}
          payerName={isBank ? payerRowValue : payerName}
          canReturnToChat={isHttpUrl(config?.returnToChatUrl)}
          closing={cancelLoading}
          onChooseMethod={chooseAnotherFundingMethod}
          onReturnToChat={() => {
            void handleCancel();
          }}
        />
      </Popup>
      <Popup
        visible={popup === "amountLimit" && amountOutOfRange}
        onClose={() => setPopup(null)}
      >
        <AmountOutOfRangeInfo
          kind={amountBoundVerdict === "below_min" ? "below_min" : "above_max"}
          method={t(`funding.methods.${pendingFundingMethod ?? ""}`, {
            defaultValue: pendingFundingMethod ?? "",
          })}
          bound={
            amountBoundVerdict === "below_min"
              ? (selectedFundingDetails?.minAmount ?? 0)
              : (selectedFundingDetails?.maxAmount ?? 0)
          }
          currency={sourceCurrency}
          cta="changeMethod"
          onClose={() => setPopup(null)}
        />
      </Popup>
      {blockedFundingCard ? (
        <Popup
          visible
          onClose={() => {
            setBlockedFundingCard(null);
            setBlockedReason(null);
          }}
        >
          <UnavailableFundingCardSheet
            card={blockedFundingCard}
            available={availableFundingMethods}
            bodyCopy={bodyCopy}
            backgroundBtn={backgroundBtn}
            textColorBtn={textColorBtn}
            reason={blockedReason ?? undefined}
            onDismiss={() => {
              setBlockedFundingCard(null);
              setBlockedReason(null);
            }}
            onChangeMethod={
              blockedReason === "railRejected" ||
              blockedReason === "railTemporarilyUnavailable"
                ? () => {
                    setBlockedFundingCard(null);
                    setBlockedReason(null);
                    setPopup("funding");
                  }
                : undefined
            }
            showHeaderClose={false}
          />
        </Popup>
      ) : null}
      {classChangedToast ? (
        <Toast
          type="info"
          duration={4000}
          message={t(
            classChangedToast === "CREDIT_CARD"
              ? "payment.classChangedCredit"
              : "payment.classChangedDebit"
          )}
          onClose={() => setClassChangedToast(null)}
        />
      ) : null}
      <Popup visible={popup === "funding"} onClose={() => setPopup(null)}>
        <SelectFundingMethodSlot
          selectedMethod={pendingFundingMethod}
          selectedPaymentMethodId={
            (fundingInstrument?.source === "saved"
              ? fundingInstrument.paymentMethodId
              : null) ||
            paymentState.payment?.cardId ||
            null
          }
          available={availableFundingMethods}
          cards={registeredCards}
          bankAccounts={bankAccounts}
          details={fundingDetails}
          hasQuote={quoteMatches}
          quoteFee={
            quoteMatches ? (quote?.totalFee ?? quote?.fee ?? 0) : null
          }
          composedOptionFee={
            isFundingMethod(pendingFundingMethod) && selectedOption
              ? selectedOption.customerFee
              : null
          }
          onPick={handleFundingPick}
          onClose={() => setPopup(null)}
          blockedRailCards={blockedRailCards}
        />
      </Popup>
      {cvvGate ? (
        <Popup
          visible
          onClose={() => {
            setCvvGate(null);
            setPrepareError(null);
          }}
        >
          <CvvPrepareGate
            last4={cvvGate.last4}
            brand={cvvGate.brand}
            loading={confirmLoading}
            error={cvvGate.error}
            onClose={() => {
              setCvvGate(null);
              setPrepareError(null);
            }}
            onSubmit={(enteredCvv) => {
              void handleConfirmClick(enteredCvv);
            }}
          />
        </Popup>
      ) : null}
    </>
  );
});
